import { GameContext, InPlay, ScopeCompletedResolver } from '../../interfaces';
import { MarketOutcome, PeriodType } from '../../enums';
import { extractNumber, extractSelection, resolveCondition } from '../parse-market.utils';

const formatHtFtPick = (pick: string): string => (pick === 'Draw' ? 'a draw' : pick);

export const soccerScopeResolver: ScopeCompletedResolver = (period, { isCompleted, inPlay }) => {
	if (isCompleted) return true;

	const currentPeriod = inPlay?.period ?? null;

	if (!currentPeriod) return false;

	switch (period) {
		case PeriodType.Game:
			return false;

		case PeriodType.Period1:
			return currentPeriod === 'HALF' || currentPeriod === '2H' || currentPeriod === '0';

		case PeriodType.Period2:
			return currentPeriod === '0';

		default:
			return false;
	}
};

export const soccerPeriodTextMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Game]: ' in the match',
	[PeriodType.Period1]: ' in the first half',
	[PeriodType.Period2]: ' in the second half',
};

const periodPrefixMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Period1]: '1st half ',
	[PeriodType.Period2]: '2nd half ',
};

const periodPrefixUpperMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Period1]: '1st Half ',
	[PeriodType.Period2]: '2nd Half ',
};

type StatType = 'corners' | 'cards' | 'shots' | 'shots_on_target';

const statKeyMap: Record<StatType, Partial<Record<PeriodType, string>>> = {
	corners: {
		[PeriodType.Game]: 'team_total_corners',
		[PeriodType.Period1]: '1st_half_team_total_corners',
		[PeriodType.Period2]: '2nd_half_team_total_corners',
	},
	cards: {
		[PeriodType.Game]: 'team_total_cards',
		[PeriodType.Period1]: '1st_half_team_total_cards',
		[PeriodType.Period2]: '2nd_half_team_total_cards',
	},
	shots: {
		[PeriodType.Game]: 'team_total_shots',
		[PeriodType.Period1]: '1st_half_team_total_shots',
		[PeriodType.Period2]: '2nd_half_team_total_shots',
	},
	shots_on_target: {
		[PeriodType.Game]: 'team_total_shots_on_target',
		[PeriodType.Period1]: '1st_half_team_total_shots_on_target',
		[PeriodType.Period2]: '2nd_half_team_total_shots_on_target',
	},
};

export const getPeriodScores = (
	gc: GameContext,
	period: PeriodType
): { home: number; away: number; isCompleted: boolean } => {
	if (period === PeriodType.Game) {
		return { home: gc.homeScore, away: gc.awayScore, isCompleted: gc.isCompleted };
	}

	if (period === PeriodType.Period1 || period === PeriodType.Period2) {
		return {
			home: gc.periodScores.home[period] ?? 0,
			away: gc.periodScores.away[period] ?? 0,
			isCompleted: gc.isScopeCompleted(period),
		};
	}

	return { home: 0, away: 0, isCompleted: false };
};

export const buildMoneylineQuestion = (team: string, period: PeriodType): string => {
	const suffix = soccerPeriodTextMap[period] ?? '';

	if (team === 'Draw') {
		if (period === PeriodType.Game) {
			return 'Will the match end in a draw?';
		}

		return `Will the ${period === PeriodType.Period1 ? 'first half' : 'second half'} end in a draw?`;
	}

	if (period === PeriodType.Game) {
		return `Will ${team} win the match?`;
	}

	return `Will ${team} win${suffix}?`;
};

export const buildDoubleChanceQuestion = (name: string, period: PeriodType): string =>
	`${periodPrefixUpperMap[period] ?? ''}Double Chance: ${name}`;

export const buildTotalGoalsQuestion = (points: number, period: PeriodType) =>
	`Will the ${periodPrefixMap[period] ?? ''}total goals be over ${points}?`;

export const buildAsianHandicapQuestion = (team: string, points: number, period: PeriodType) => {
	const periodPrefix: Partial<Record<PeriodType, string>> = {
		[PeriodType.Period1]: '1H: ',
		[PeriodType.Period2]: '2H: ',
	};

	return `${periodPrefix[period] ?? ''}${team} Asian Handicap ${
		points > 0 ? `+${points}` : points
	}`;
};

export const buildTeamTotalQuestion = (team: string, points: number, period: PeriodType) =>
	`Will ${team} ${periodPrefixMap[period] ?? ''}goals be over ${points}?`;

export const buildBothTeamsToScoreQuestion = (period: PeriodType) => {
	const periodPrefix: Partial<Record<PeriodType, string>> = {
		[PeriodType.Period1]: 'in the 1st half',
		[PeriodType.Period2]: 'in the 2nd half',
	};

	return `Will both teams score${periodPrefix[period] ? ` ${periodPrefix[period]}` : ''}?`;
};

export const buildCorrectScoreQuestion = (score: string, period: PeriodType): string => {
	const periodPhraseMap: Partial<Record<PeriodType, string>> = {
		[PeriodType.Game]: 'final',
		[PeriodType.Period1]: '1st half',
		[PeriodType.Period2]: '2nd half',
	};

	const phrase = periodPhraseMap[period] ?? 'final';
	return `Will the ${phrase} score be ${score}?`;
};

export const buildHalftimeFulltimeQuestion = (selection: string): string => {
	const [ht, ft] = selection.split('::').map((s) => s.trim());
	if (!ht || !ft) return `Halftime / Fulltime: ${selection}`;
	return `Will the game be ${formatHtFtPick(ht)} at halftime and ${formatHtFtPick(ft)} at full time?`;
};

export const buildTotalStatQuestion = (points: number, period: PeriodType, label: string) =>
	`Will ${periodPrefixMap[period] ?? ''}total ${label} be over ${points}?`;

export const buildTeamTotalStatQuestion = (
	team: string,
	points: number,
	period: PeriodType,
	label: string
) => `Will ${team} ${periodPrefixMap[period] ?? ''}${label} be over ${points}?`;

export const resolveDoubleChance = (
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const selection = q.split(': ').pop()?.trim();

	if (!selection) return [MarketOutcome.Pending];

	const homeTeam = gc.homeTeam.toLowerCase();
	const awayTeam = gc.awayTeam.toLowerCase();

	const actual = home > away ? homeTeam : home < away ? awayTeam : 'draw';

	const selections = selection.split(/ or /i).map((s) => s.trim().toLowerCase());

	return [selections.includes(actual) ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveAsianHandicap = (
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const teamWithPeriod = extractSelection(q, '', ['Asian Handicap']);

	if (!teamWithPeriod) return [MarketOutcome.Pending];

	const team = teamWithPeriod.replace(/1H:|2H:|1st half|2nd half/i, '').trim();

	if (!team) return [MarketOutcome.Pending];

	const points = extractNumber(q);

	const isHome = team === gc.homeTeam;
	const isAway = team === gc.awayTeam;

	if (!isHome && !isAway) return [MarketOutcome.Pending];

	const teamScore = isHome ? home : away;
	const opponentScore = isHome ? away : home;

	const adjustedScore = teamScore + points;

	if (adjustedScore > opponentScore) return [MarketOutcome.Won];

	if (adjustedScore < opponentScore) return [MarketOutcome.Lost];

	return [MarketOutcome.Pending];
};

export const resolveTeamTotal = (
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	const teamWithPeriod = extractSelection(q, 'Will ', ['goals']);

	if (!teamWithPeriod) return [MarketOutcome.Pending, 0];

	const team = teamWithPeriod.replace(/1st half|2nd half/i, '').trim();

	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const isHome = team === gc.homeTeam;
	const total = isHome ? home : away;

	return [resolveCondition(total >= threshold, isCompleted), total];
};

export const resolveBothTeamsToScore = (
	_q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const condition = home > 0 && away > 0;

	return [resolveCondition(condition, isCompleted)];
};

export const resolveCorrectScore = (
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const match = q.match(/(\d+):(\d+)/);

	if (!match) return [MarketOutcome.Lost];

	const [expectedHome, expectedAway] = match.slice(1).map(Number);

	return [resolveCondition(home === expectedHome && away === expectedAway, isCompleted)];
};

const normalizeHtFtPick = (pick: string): string => {
	if (pick === 'a draw') return 'Draw';
	return pick;
};

export const resolveHalftimeFulltime = (q: string, gc: GameContext): [MarketOutcome] => {
	const match = q.match(/Will the game be (.+?) at halftime and (.+?) at full time\?/);
	if (!match) return [MarketOutcome.Pending];

	const htPick = normalizeHtFtPick(match[1].trim());
	const ftPick = normalizeHtFtPick(match[2].trim());

	const {
		home: htHome,
		away: htAway,
		isCompleted: htDone,
	} = getPeriodScores(gc, PeriodType.Period1);

	if (!htDone) return [MarketOutcome.Pending];

	const actualHT = htHome === htAway ? 'Draw' : htHome > htAway ? gc.homeTeam : gc.awayTeam;

	if (actualHT !== htPick) return [MarketOutcome.Lost];

	if (!gc.isCompleted) return [MarketOutcome.Pending];

	const actualFT =
		gc.homeScore === gc.awayScore
			? 'Draw'
			: gc.homeScore > gc.awayScore
				? gc.homeTeam
				: gc.awayTeam;

	return [actualFT === ftPick ? MarketOutcome.Won : MarketOutcome.Lost];
};

const getStatTotal = (r: any, stat: StatType, period: PeriodType): number => {
	const stats = r?.score?.market_stats;
	const key = statKeyMap[stat]?.[period];

	if (!stats || !key) return 0;

	return (stats.home?.[key] ?? 0) + (stats.away?.[key] ?? 0);
};

const getTeamStat = (
	r: any,
	gc: GameContext,
	stat: StatType,
	team: string,
	period: PeriodType
): number => {
	const stats = r?.score?.market_stats;
	const key = statKeyMap[stat]?.[period];

	if (!stats || !key) return 0;

	const side = team === gc.homeTeam ? stats.home : stats.away;

	return side?.[key] ?? 0;
};

export const resolveTotalStat = (
	q: string,
	gc: GameContext,
	r: any,
	stat: StatType,
	period: PeriodType
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	const total = getStatTotal(r, stat, period);

	return [resolveCondition(total > threshold, gc.isScopeCompleted(period)), total];
};

export const resolveTeamTotalStat = (
	q: string,
	gc: GameContext,
	r: any,
	stat: StatType,
	period: PeriodType
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);

	const team = q
		.replace(/^Will /, '')
		.replace(/ corners| cards| shots| shots on target/i, '')
		.replace(/ 1st half| 2nd half/, '')
		.split(' be over')[0]
		.trim();

	const total = getTeamStat(r, gc, stat, team, period);

	return [resolveCondition(total > threshold, gc.isScopeCompleted(period)), total];
};

const HALF_MIN = 45;
const REG_MIN = HALF_MIN * 2;

export const soccerTimeProgressResolver = (inPlay: InPlay): number | null => {
	const { period, clock } = inPlay;
	if (!period) return null;
	if (period === '0') return 1;
	if (period === 'HALF') return 0.5;

	const minute = Number(clock);
	if (isNaN(minute)) return null;

	return period === '1H'
		? Math.min(minute, HALF_MIN) / REG_MIN
		: period === '2H'
			? (HALF_MIN + Math.min(minute - HALF_MIN, HALF_MIN)) / REG_MIN
			: null;
};
